import {
  collection,
  doc,
  serverTimestamp,
  type CollectionReference,
  type DocumentReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type PartialWithFieldValue,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { StreamInterface } from 'tricklejs/dist/types'
import { modelListStream } from '../../firestore-mobx/stream'
import { setDocWithError } from '../../firestore-mobx/fetch'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { AssignmentRubric } from '../../models/AssignmentRubric'
import type {
  FirestoreAssignmentRubric,
  FirestoreAssignmentRubricWrite,
} from './schema'
import { schema, writeSchema } from './schema'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreAssignmentRubric> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreAssignmentRubric>) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
): CollectionReference<FirestoreAssignmentRubric, DocumentData> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'assignment_rubric'
  ).withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  {
    sectionId,
    assignmentId,
    rubricId,
  }: { sectionId: string; assignmentId: string; rubricId: string }
): DocumentReference<FirestoreAssignmentRubric, DocumentData> => {
  return doc(getColRef(firestore, { sectionId, assignmentId }), rubricId)
}

/** Stream of all AssignmentRubrics for an assignment. */
export const getAssignmentRubricsStream = (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
): StreamInterface<AssignmentRubric[]> => {
  const ref = getColRef(repository.firestore, params)
  return modelListStream(repository, ref, AssignmentRubric)
}

/**
 * Upsert a single AssignmentRubric. The document id matches the originating
 * SlideRubric id. Merges so callers can update only weight/scoreMapping without
 * clobbering the copied rubric metadata.
 */
export const upsertAssignmentRubric = async (
  repository: FirebaseRepository,
  {
    sectionId,
    assignmentId,
    rubricId,
    data,
  }: {
    sectionId: string
    assignmentId: string
    rubricId: string
    data: Partial<Omit<FirestoreAssignmentRubricWrite, 'updatedAt'>>
  }
): Promise<void> => {
  const ref = getDocRef(repository.firestore, {
    sectionId,
    assignmentId,
    rubricId,
  })
  await setDocWithError(
    ref,
    { ...data, updatedAt: serverTimestamp() },
    { merge: true, errorName: 'UpsertAssignmentRubricError' }
  )
}
